/** Shared ticket helpers (Portal + Dashboard). */

export const TICKET_NUMBER_REGEX = /^TKT-\d{4}-\d{6}$/i;

export function pickTranslatedName(obj, lang) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  const names = obj.name || obj.title || obj;
  if (typeof names === "string") return names;
  return names?.[lang] ?? names?.en ?? names?.ar ?? "";
}

export function getStatusCode(status) {
  if (!status) return "";
  if (typeof status === "string") return status.toLowerCase();
  return String(status.code || status.slug || status.value || "").toLowerCase();
}

/** MDI icon class for ticket category pills */
export function categoryPillIcon(category) {
  const code = getStatusCode(category);
  const icons = {
    technical: "mdi-wrench-outline",
    billing: "mdi-cash-multiple",
    payment: "mdi-credit-card-outline",
    account: "mdi-account-outline",
    general: "mdi-help-circle-outline",
    consultation: "mdi-briefcase-outline",
    complaint: "mdi-alert-circle-outline",
    feature: "mdi-lightbulb-outline",
    other: "mdi-dots-horizontal",
  };
  return icons[code] || "mdi-tag-outline";
}

export function statusBadgeClasses(status, { unassigned = false } = {}) {
  const code = getStatusCode(status);
  if (code === "open" && unassigned) {
    return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
  }
  switch (code) {
    case "open":
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800";
    case "in_progress":
      return "bg-secondary/10 text-secondary border border-secondary/50";
    case "resolved":
      return "bg-primary/10 text-primary border border-primary/50";
    case "closed":
      return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
    case "escalated":
      return "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800";
    default:
      return "bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-200 border border-slate-300/70 dark:border-slate-700";
  }
}

export function statusDotClass(status, { unassigned = false } = {}) {
  const code = getStatusCode(status);
  if (code === "open" && unassigned) return "bg-emerald-500";
  switch (code) {
    case "open":
      return "bg-emerald-500";
    case "in_progress":
      return "bg-secondary";
    case "resolved":
      return "bg-primary";
    case "closed":
      return "bg-slate-400";
    case "escalated":
      return "bg-rose-500";
    default:
      return "bg-slate-400";
  }
}

/** MDI icon for ticket status badges (matches filter pill icons). */
export function statusIconClass(status, { unassigned = false } = {}) {
  const code = getStatusCode(status);
  if (code === "open" && unassigned) return "mdi-star-four-points-outline";
  switch (code) {
    case "open":
      return "mdi-inbox-outline";
    case "in_progress":
      return "mdi-progress-clock";
    case "resolved":
      return "mdi-check-circle-outline";
    case "closed":
      return "mdi-archive-outline";
    case "escalated":
      return "mdi-arrow-up-bold-circle-outline";
    default:
      return "mdi-label-outline";
  }
}

/*
export function priorityBadgeClasses(priority) {
  const code = String(priority?.code || priority?.slug || "").toLowerCase();
  switch (code) {
    case "urgent":
      return "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
    case "high":
      return "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-900/40 dark:bg-orange-900/20 dark:text-orange-300";
    case "medium":
      return "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200";
    case "low":
      return "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
  }
}
*/

export function formatTicketDateTime(value, lang = "ar") {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

const RELATIVE_UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

/** Localised "3 hours ago" style label, falling back to the absolute date. */
export function formatTicketRelativeTime(value, lang = "ar") {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) {
    return formatTicketDateTime(value, lang);
  }

  try {
    const formatter = new Intl.RelativeTimeFormat(
      lang === "ar" ? "ar" : "en",
      { numeric: "auto" },
    );
    const seconds = Math.round((date.getTime() - Date.now()) / 1000);
    for (const [unit, secondsPerUnit] of RELATIVE_UNITS) {
      if (Math.abs(seconds) >= secondsPerUnit) {
        return formatter.format(Math.round(seconds / secondsPerUnit), unit);
      }
    }
    return formatter.format(0, "minute");
  } catch {
    return formatTicketDateTime(value, lang);
  }
}

/** Stable per-calendar-day key used to group timeline and comment entries. */
export function ticketDayKey(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "unknown";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/** 0 = today, 1 = yesterday, -1 = any other day (or unparseable). */
export function ticketDayOffset(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return -1;

  const today = new Date();
  if (ticketDayKey(date) === ticketDayKey(today)) return 0;

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (ticketDayKey(date) === ticketDayKey(yesterday)) return 1;

  return -1;
}

export function formatTicketDayDate(value, lang = "ar") {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  try {
    return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
}

export function creatorDisplayName(ticket, lang) {
  const name =
    ticket?.creator?.name ||
    ticket?.created_by?.name ||
    ticket?.user?.name ||
    ticket?.creator_name ||
    "";
  const type = ticket?.creator_type || ticket?.created_by_type || "";
  if (!name) return "—";
  if (!type) return name;
  const typeLabel =
    type === "client"
      ? lang === "ar"
        ? "عميل"
        : "Client"
      : lang === "ar"
        ? "موظف"
        : "Staff";
  return `${name} (${typeLabel})`;
}

export function toFormData(fields) {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    if (key === "attachments" && Array.isArray(value)) {
      for (const file of value) {
        if (file) form.append("attachments[]", file);
      }
    } else {
      form.append(key, String(value));
    }
  }
  return form;
}

const HANDLER_ROLE_HINTS = [
  "staff",
  "employee",
  "handler",
  "support",
  "assignee",
  "agent",
  "admin",
  "موظف",
  "دعم",
];

const CREATOR_ROLE_HINTS = [
  "client",
  "creator",
  "requester",
  "customer",
  "user",
  "عميل",
];

/** The API returns the comment author under `sender`; the rest are fallbacks. */
export function commentAuthorId(comment) {
  return (
    comment?.sender?.id ??
    comment?.sender_id ??
    comment?.user?.id ??
    comment?.user_id ??
    comment?.author?.id ??
    comment?.creator?.id ??
    null
  );
}

export function commentAuthorName(comment) {
  return String(
    comment?.sender?.name ||
      comment?.sender?.full_name ||
      (typeof comment?.sender === "string" ? comment.sender : "") ||
      comment?.sender_name ||
      comment?.user?.name ||
      comment?.user?.full_name ||
      comment?.author?.name ||
      comment?.author?.full_name ||
      comment?.creator?.name ||
      comment?.creator?.full_name ||
      comment?.user_name ||
      comment?.name ||
      "",
  ).trim();
}

/** True when both sides expose an id or a name that refer to the same person. */
function isSamePerson(comment, party) {
  if (!party) return false;

  const authorId = commentAuthorId(comment);
  if (authorId != null && party.id != null) {
    return String(authorId) === String(party.id);
  }

  const authorName = commentAuthorName(comment).toLowerCase();
  const partyName = String(party.name || "").trim().toLowerCase();
  return Boolean(authorName && partyName && authorName === partyName);
}

/**
 * Portal comment sender: ticket creator (client) vs ticket handler (staff).
 * The ticket parties let the caller resolve authorship when the API omits role
 * fields, which is otherwise indistinguishable from a staff reply.
 */
export function getCommentSenderKind(
  comment,
  ticketCreator = null,
  ticketHandler = null,
) {
  if (!comment) return "handler";

  if (isSamePerson(comment, ticketCreator)) return "creator";
  if (isSamePerson(comment, ticketHandler)) return "handler";

  const role = String(
    comment.user_type ||
      comment.actor_type ||
      comment.sender?.type ||
      comment.sender?.role ||
      comment.user?.type ||
      comment.sender_type ||
      comment.created_by_type ||
      comment.role ||
      comment.author?.type ||
      "",
  ).toLowerCase();

  if (
    comment.is_handler ||
    comment.is_staff ||
    comment.is_assignee ||
    HANDLER_ROLE_HINTS.some((hint) => role.includes(hint))
  ) {
    return "handler";
  }

  if (
    comment.is_creator ||
    comment.is_requester ||
    comment.is_client ||
    CREATOR_ROLE_HINTS.some((hint) => role.includes(hint))
  ) {
    return "creator";
  }

  if (comment.is_mine || comment.isMine || comment.mine) {
    return "creator";
  }

  return "handler";
}
